import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {
  BriefcaseBusiness,
  Check,
  Contact,
  ExternalLink,
  X,
} from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import AssignBrokersToSubstantiation from 'components/admin/AssignBrokersToSubstantiation'
import { DisplayTextOnHoverTooltip } from 'components/brokers/needs_attention/NeedsAttentionTable'
import SubstantiationContext from 'components/context/SubstantiationContext'
import {
  getSubstantiationsQueryKey,
  type SubstantiationsFilters,
  useGetSubstantiations,
} from 'components/hooks/substantiations/useGetSubstantiations'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable, {
  getHeaderCheckboxState,
  type PaginationMetadata,
} from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { adminSubstantiations } from 'rails_routes'
import { Badge } from 'slab'
import { getLocaleDateString } from 'utils'
import { type AdminSubstantiation } from 'utils/web_api.schemas'

import { PaymentMethodBadge } from './PaymentMethodBadge'
import { type User } from './types'

export const MAX_NUMBER_OF_SELECTED_ITEMS = 50

const columnHelper = createColumnHelper<AdminSubstantiation>()

// Simple badge component for review status
const SubstantiationReviewStatusBadge = ({
  status,
}: {
  status: AdminSubstantiation['review_status']
}) => {
  const statusConfig = {
    waiting_for_review: {
      label: 'Waiting For Review',
      type: 'caution' as const,
    },
    approved: { label: 'Approved', type: 'success' as const },
    rejected: { label: 'Rejected', type: 'warning' as const },
    inactive: { label: 'Inactive', type: 'neutral' as const },
  }

  return (
    <Badge type={statusConfig[status].type}>{statusConfig[status].label}</Badge>
  )
}

const TransactionTypeBadge = ({
  transactionType,
}: {
  transactionType: AdminSubstantiation['transaction_type']
}) => {
  const transactionTypeConfig = {
    medical: { label: 'Medical', type: 'success' as const },
    ancillary: { label: 'Ancillary', type: 'neutral' as const },
    unknown: { label: 'Unknown', type: 'caution' as const },
    qme: { label: 'QME', type: 'info' as const },
  }

  return (
    <Badge type={transactionTypeConfig[transactionType].type}>
      {transactionTypeConfig[transactionType].label}
    </Badge>
  )
}

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={getHeaderCheckboxState(table)}
            onClick={table.getToggleAllPageRowsSelectedHandler()}
          />
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onClick={row.getToggleSelectedHandler()}
          />
        </div>
      )
    },
  }),
  columnHelper.display({
    header: 'Actions',
    cell: ({ row, table }) => {
      return (
        <div className='flex items-center gap-4'>
          <DisplayTooltipOnHover text='Open in a new tab'>
            <div
              className='cursor-pointer h-full'
              onClick={(e) => {
                e.preventDefault()
                window.open(
                  adminSubstantiations.show({ id: row.original.id }),
                  '_blank',
                  'noopener noreferrer'
                )
              }}
            >
              <ExternalLink
                size={20}
                className='stroke-current text-gray-600'
              />
            </div>
          </DisplayTooltipOnHover>
          <DisplayTooltipOnHover text='Filter this member'>
            <div
              className='cursor-pointer h-full'
              onClick={() => {
                const meta = table.options.meta as any
                if (meta?.setFilters) {
                  meta.setFilters((prevFilters: SubstantiationsFilters) => ({
                    ...prevFilters,
                    member_ids: [row.original.member_id || ''],
                  }))
                }
              }}
            >
              <Contact size={20} className='stroke-current text-gray-600' />
            </div>
          </DisplayTooltipOnHover>
          <DisplayTooltipOnHover text='Filter this employer'>
            <div
              className='cursor-pointer h-full'
              onClick={() => {
                const meta = table.options.meta as any
                if (meta?.setFilters) {
                  meta.setFilters((prevFilters: SubstantiationsFilters) => ({
                    ...prevFilters,
                    employer_ids: [row.original.employer_id || ''],
                  }))
                }
              }}
            >
              <BriefcaseBusiness
                size={20}
                className='stroke-current text-gray-600'
              />
            </div>
          </DisplayTooltipOnHover>
        </div>
      )
    },
  }),
  columnHelper.accessor('created_at', {
    header: (info) => (
      <SortableHeader<AdminSubstantiation> {...info} headerText='Created' />
    ),
    cell: (info) => getLocaleDateString(new Date(info.getValue() || '')),
    enableSorting: true,
  }),
  columnHelper.accessor('brokers', {
    header: 'Assignees',
    cell: ({ getValue, row }) => {
      const { id } = row.original
      return (
        <AssignBrokersToSubstantiation
          id={id}
          assignedBrokers={getValue() || []}
        />
      )
    },
  }),
  columnHelper.accessor('reviewed_at', {
    header: (info) => (
      <SortableHeader<AdminSubstantiation> {...info} headerText='Reviewed' />
    ),
    cell: (info) => {
      const value = info.getValue()
      return value ? getLocaleDateString(new Date(value)) : 'N/A'
    },
    enableSorting: true,
  }),
  columnHelper.accessor('reviewed_by_name', {
    header: 'Reviewed By',
    cell: (info) => info.getValue() || 'N/A',
  }),
  columnHelper.accessor('review_status', {
    header: (info) => (
      <SortableHeader<AdminSubstantiation> {...info} headerText='Status' />
    ),
    cell: (info) => (
      <SubstantiationReviewStatusBadge status={info.getValue()} />
    ),
    enableSorting: true,
  }),

  columnHelper.accessor('transaction_type', {
    header: 'Transaction Type',
    cell: (info) => <TransactionTypeBadge transactionType={info.getValue()} />,
  }),
  columnHelper.accessor('payment_method', {
    header: 'Payment Method',
    cell: (info) => <PaymentMethodBadge paymentMethod={info.getValue() || ''} />,
  }),
  columnHelper.accessor('member_name', {
    header: 'Member',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
  columnHelper.accessor('amount', {
    header: 'Amount',
    cell: (info) => `$${parseFloat(info.getValue().value || '0').toFixed(2)}`,
  }),
  columnHelper.accessor('medical_care', {
    header: 'Medical Care',
    cell: (info) => {
      const value = info.getValue()
      if (!value) return 'N/A'
      return value.replace('medical_care_', '').replace('_', ' ')
    },
  }),
  columnHelper.accessor('has_receipts', {
    header: 'Receipts',
    cell: (info) =>
      info.getValue()
        ? (
          <Check size={20} className='stroke-current text-green-600' />
          )
        : (
          <X size={20} className='stroke-current text-red-600' />
          ),
  }),
  columnHelper.accessor('receipt_last_uploaded_at', {
    header: 'Receipt Last Uploaded',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue() || ''))
        : 'N/A',
  }),
  columnHelper.accessor('employer_name', {
    header: 'Employer',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
]

type SubstantiationsTableProps = {
  filters: SubstantiationsFilters
  setFilters: Dispatch<SetStateAction<SubstantiationsFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  rowSelection: RowSelectionState
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>
  currentUser?: User
}

const SubstantiationsTable = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
}: SubstantiationsTableProps) => {
  const {
    data: substantiations,
    isLoading,
    isError,
  } = useGetSubstantiations({ filters, pagination, sorting })

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const table = useReactTable({
    data: substantiations?.data || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => {
      return (
        Object.keys(rowSelection).length < MAX_NUMBER_OF_SELECTED_ITEMS ||
        rowSelection[row.id] === true
      )
    },
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const newSelection =
          typeof updater === 'function' ? updater(prev) : updater
        const selectedRowIds = Object.keys(newSelection)
        if (selectedRowIds.length > MAX_NUMBER_OF_SELECTED_ITEMS) {
          return prev // Keep the previous selection if over the limit
        }
        return newSelection
      })
    },
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    meta: {
      setFilters,
    } as any,
  })

  useEffect(() => {
    if (substantiations) {
      setPaginationMetadata(substantiations.pagination)
    }
  }, [substantiations?.pagination])

  return (
    <SubstantiationContext.Provider
      value={{
        setFilters,
        getSubstantiationsQueryKey: getSubstantiationsQueryKey({
          filters,
          pagination,
          sorting,
        }),
      }}
    >
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='There are no substantiations that need attention.'
        paginationLabelSingular='substantiation'
        paginationMetadata={paginationMetadata}
        setSorting={setSorting}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </SubstantiationContext.Provider>
  )
}

export default SubstantiationsTable
